import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listCoupons, deactivateCoupon, describeCoupon, type Coupon } from '@/lib/promotions';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

/**
 * /admin/promotions — list of coupons. Click into a coupon (or "+ New") to
 * create/edit. Pre-Stripe these are applied via the per-org subscription
 * editor; post-Stripe they sync to Stripe Coupons + Promotion Codes.
 */
export default function PromotionsList() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true); setError(null);
    try { setCoupons(await listCoupons()); }
    catch (e) { setError(humanize(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, []);

  async function deactivate(id: string, code: string) {
    const reason = prompt(`Deactivate coupon ${code}? Reason for the audit log:`);
    if (reason === null) return;
    try { await deactivateCoupon(id, reason); refresh(); }
    catch (e) { setError(humanize(e)); }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading coupons…</p>;
  if (error)   return <p className="text-sm text-red-600">{error}</p>;

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Promotions</h2>
          <p className="text-sm text-muted-foreground">
            Coupons + discount codes. Pre-Stripe: applied per-subscription via the cockpit. Post-Stripe: sync to Stripe Coupons + Promotion Codes.
          </p>
        </div>
        <Link to="/admin/promotions/new" className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90">
          + New coupon
        </Link>
      </header>

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm border-collapse min-w-[760px]">
          <thead>
            <tr className="border-b border-border text-left">
              <Th>Code</Th>
              <Th>Name</Th>
              <Th>Discount</Th>
              <Th>Redemptions</Th>
              <Th>Expires</Th>
              <Th>Status</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {coupons.map((c, i) => (
              <tr key={c.id} className={`border-b border-border ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                <Td>
                  <span className="font-mono text-xs" style={{ color: 'hsl(var(--brand-navy))' }}>{c.code}</span>
                </Td>
                <Td>{c.name}</Td>
                <Td className="text-xs">{describeCoupon(c)}</Td>
                <Td className="text-xs">
                  {c.times_redeemed}{c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}
                </Td>
                <Td className="text-xs">{c.redeem_by ? formatDate(c.redeem_by) : '—'}</Td>
                <Td className="text-xs">
                  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 ${
                    c.active ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-muted/40 border-border text-muted-foreground'
                  }`}>
                    {c.active ? 'Active' : 'Inactive'}
                  </span>
                </Td>
                <Td>
                  {c.active && (
                    <button onClick={() => deactivate(c.id, c.code)} className="text-xs underline text-red-700">
                      Deactivate
                    </button>
                  )}
                </Td>
              </tr>
            ))}
            {coupons.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-sm text-muted-foreground">
                No coupons yet. Click + New coupon to create one.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th scope="col" className="p-3 text-xs uppercase tracking-wider text-muted-foreground font-medium">{children}</th>;
}
function Td({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return <td className={`p-3 align-top ${className}`}>{children}</td>;
}
